import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const ClientsServicesManager = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filter, setFilter] = useState('all'); // all, client, service

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await api.get('/admin/clients-services');
      setItems(response.data || []);
    } catch (err) {
      setError('Failed to load clients/services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (itemData) => {
    try {
      await api.post('/admin/clients-services', itemData);
      setSuccess(`${itemData.type === 'client' ? 'Client' : 'Service'} created!`);
      setShowForm(false);
      fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create item');
    }
  };

  const handleUpdate = async (id, itemData) => {
    try {
      await api.put(`/admin/clients-services/${id}`, itemData);
      setSuccess('Item updated!');
      setEditingItem(null);
      fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await api.delete(`/admin/clients-services/${id}`);
      setSuccess('Item deleted!');
      fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/admin/clients-services/${id}/publish`);
      setSuccess('Item published!');
      fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish item');
    }
  };

  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Clients & Services Manager</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              + Add New
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {/* Filter Tabs */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setFilter('all')}
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            >
              All ({items.length})
            </button>
            <button
              onClick={() => setFilter('client')}
              className={`btn ${filter === 'client' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Clients ({items.filter(i => i.type === 'client').length})
            </button>
            <button
              onClick={() => setFilter('service')}
              className={`btn ${filter === 'service' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Services ({items.filter(i => i.type === 'service').length})
            </button>
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)', padding: '40px' }}>
              No items yet. Add your first {filter === 'all' ? 'client or service' : filter}!
            </p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {item.logo_url && (
                          <img
                            src={item.logo_url}
                            alt={item.name}
                            style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '4px' }}
                          />
                        )}
                        <div>
                          <strong>{item.name}</strong>
                          {item.description && (
                            <>
                              <br />
                              <small style={{ color: 'var(--secondary-color)' }}>
                                {item.description.substring(0, 80)}...
                              </small>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        backgroundColor: item.type === 'client' ? '#e3f2fd' : '#f3e5f5',
                        color: item.type === 'client' ? '#1976d2' : '#7b1fa2',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        {item.type}
                      </span>
                    </td>
                    <td>{item.order_index}</td>
                    <td>
                      <span className={`status-badge ${item.is_published ? 'status-published' : 'status-draft'}`}>
                        {item.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => setEditingItem(item)} className="btn btn-secondary">
                          Edit
                        </button>
                        {!item.is_published && (
                          <button onClick={() => handlePublish(item.id)} className="btn btn-success">
                            Publish
                          </button>
                        )}
                        <button onClick={() => handleDelete(item.id)} className="btn btn-danger">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {(showForm || editingItem) && (
          <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingItem(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
              <ItemForm
                item={editingItem}
                onSave={editingItem ? (data) => handleUpdate(editingItem.id, data) : handleCreate}
                onCancel={() => { setShowForm(false); setEditingItem(null); }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    type: item?.type || 'client',
    name: item?.name || '',
    description: item?.description || '',
    logo_url: item?.logo_url || '',
    website_url: item?.website_url || '',
    order_index: item?.order_index || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Type *</label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          required
        >
          <option value="client">Client</option>
          <option value="service">Service</option>
        </select>
      </div>

      <div className="form-group">
        <label>Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="Client or service name"
        />
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows="4"
          placeholder="Brief description..."
        />
      </div>

      <div className="form-group">
        <label>Logo/Image URL</label>
        <input
          type="text"
          value={formData.logo_url}
          onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
          placeholder="/uploads/logo.jpg"
        />
        {formData.logo_url && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={formData.logo_url}
              alt="Preview"
              style={{ maxWidth: '200px', borderRadius: '4px' }}
            />
          </div>
        )}
        <small style={{ color: 'var(--secondary-color)' }}>
          Upload image in Media Library and paste URL here
        </small>
      </div>

      {formData.type === 'client' && (
        <div className="form-group">
          <label>Website URL</label>
          <input
            type="url"
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
            placeholder="https://client-website.com"
          />
        </div>
      )}

      <div className="form-group">
        <label>Order Index</label>
        <input
          type="number"
          value={formData.order_index}
          onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
          min="0"
        />
      </div>

      <div className="button-group">
        <button type="submit" className="btn btn-primary">
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ClientsServicesManager;
